import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { MasterRow } from "../spec";

interface NextLabel {
  yearCode: string;
  year: string;
  next: string;
  last: string | null;
}

/**
 * A domino line's next pouch label per year code, under its edit sheet. Read-only: the Packing Slip
 * suggests this number and moves it on when a range is saved; nobody edits the sequence by hand
 * (docs/designs/packaging-module.md, D5). A label is plant + line letter + year code + 7 digits.
 * A new (unsaved) line has no id yet, so nothing is fetched.
 */
export function NextLabels({ row }: { row: MasterRow }) {
  const id = typeof row.id === "number" ? row.id : null;
  const q = useQuery({
    queryKey: ["masters", "processing-resources", id, "next-labels"],
    queryFn: () =>
      api<{ items: NextLabel[] }>(
        `/masters/processing-resources/${id}/next-labels`
      ),
    enabled: id !== null,
  });
  const labels = q.data?.items ?? [];
  const used = labels.some((l) => l.last);
  const current = labels.find((l) => !l.last);
  return (
    <section className="mt-4 rounded-md border bg-muted/30 p-3">
      <h3 className="text-sm font-medium">Next pouch label</h3>
      {!used ? (
        <p className="text-muted-foreground mt-1 text-xs">
          No packing slip has used this line yet.
          {current ? (
            <>
              {" "}
              First label this year:{" "}
              <span className="font-mono">{current.next}</span>
            </>
          ) : (
            " The first label of a year starts at 0000001."
          )}
        </p>
      ) : (
        <table className="mt-2 w-full text-sm">
          <thead className="text-muted-foreground text-xs">
            <tr>
              <th className="py-1 text-left font-normal">Year</th>
              <th className="py-1 text-left font-normal">Last used</th>
              <th className="py-1 text-left font-normal">Next</th>
            </tr>
          </thead>
          <tbody>
            {labels.map((l) => (
              <tr key={l.yearCode} className="border-t">
                <td className="py-1">
                  {l.year}{" "}
                  <span className="text-muted-foreground">({l.yearCode})</span>
                </td>
                <td className="py-1 font-mono">{l.last ?? "—"}</td>
                <td className="py-1 font-mono font-medium">{l.next}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-muted-foreground mt-2 text-xs">
        Set by the Packing Slip when a label range is saved. Operators may type
        a different start on the slip; the next suggestion follows it.
      </p>
    </section>
  );
}
